'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import RequestTransportForm from '@/components/RequestTransportForm';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

function LazyMap() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="rounded-xl overflow-hidden border border-border h-56 bg-muted"
      aria-label="Map of Kapra Medical Transportation — 5 Newark Pompton Turnpike, Riverdale, NJ"
    >
      {loaded ? (
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3014.5!2d-74.3!3d41.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c2fd3e5a5a5a5a%3A0x5a5a5a5a5a5a5a5a!2s5+Newark+Pompton+Turnpike%2C+Riverdale%2C+NJ+07457!5e0!3m2!1sen!2sus!4v1"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Kapra Medical Transportation location — 5 Newark Pompton Turnpike, Riverdale, NJ"
        />
      ) : (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <Icon name="MapPinIcon" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">5 Newark Pompton Turnpike</p>
            <p className="text-xs text-muted-foreground">Riverdale, NJ 07457</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContactPageClient() {
  return (
    <>
      <Header />
      <main id="main-content" tabIndex={-1}>
        {/* Hero */}
        <section className="bg-primary pt-32 pb-16 px-6 md:px-10" aria-labelledby="contact-heading">
          <div className="max-w-screen-xl mx-auto">
            <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Contact' }]} />
            <span className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4 block">Get In Touch</span>
            <h1 id="contact-heading" className="text-section-xl font-display font-light text-white mb-4">
              Get in <span className="italic text-white/70">Touch</span>
            </h1>
            <p className="text-white/70 max-w-xl text-base leading-relaxed">
              Call us at{' '}
              <a href="tel:+15513269573" className="text-white font-medium hover:text-white/80 transition-colors">(551) 326-9573</a>,
              email{' '}
              <a href="mailto:info@kapra-ems.com" className="text-white font-medium hover:text-white/80 transition-colors">info@kapra-ems.com</a>,
              or request transport below.
            </p>
          </div>
        </section>

        {/* Callout strip */}
        <div className="bg-secondary border-b border-border px-6 md:px-10 py-4">
          <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row gap-3 sm:gap-8 text-sm text-muted-foreground">
            <span>
              For facility partnership inquiries,{' '}
              <Link href="/facilities" className="text-primary font-medium hover:text-accent transition-colors">
                visit our Facilities page →
              </Link>
            </span>
            <span>
              Interested in joining our team?{' '}
              <Link href="/careers" className="text-primary font-medium hover:text-accent transition-colors">
                See open positions →
              </Link>
            </span>
          </div>
        </div>

        {/* Two-column layout: form left, contact right */}
        <section className="py-16 px-6 md:px-10 bg-background" aria-label="Contact information and request form">
          <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            {/* Left — Request Transport form */}
            <div className="lg:col-span-7 bg-card rounded-3xl border border-border p-8 md:p-10 shadow-card">
              <h2 className="font-display text-2xl font-medium text-primary mb-2">Request Transport</h2>
              <p className="text-sm text-muted-foreground mb-8">
                All fields marked with <span className="text-red-500">*</span> are required.
              </p>
              <RequestTransportForm />
            </div>

            {/* Right — Contact details */}
            <div className="lg:col-span-5 space-y-5">
              <h2 className="font-display text-2xl font-medium text-primary">Contact Details</h2>

              <a
                href="tel:+15513269573"
                className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border hover:border-accent/40 hover:shadow-card transition-all group"
                aria-label="Phone: (551) 326-9573"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-accent/15 transition-colors">
                  <Icon name="PhoneIcon" size={18} className="text-primary group-hover:text-accent transition-colors" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Phone</p>
                  <p className="text-sm font-semibold text-foreground">(551) 326-9573</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Click to call</p>
                </div>
              </a>

              <a
                href="mailto:info@kapra-ems.com"
                className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border hover:border-accent/40 hover:shadow-card transition-all group"
                aria-label="Email: info@kapra-ems.com"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-accent/15 transition-colors">
                  <Icon name="EnvelopeIcon" size={18} className="text-primary group-hover:text-accent transition-colors" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Email</p>
                  <p className="text-sm font-semibold text-foreground">info@kapra-ems.com</p>
                </div>
              </a>

              <div className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon name="MapPinIcon" size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Address</p>
                  <address className="not-italic text-sm font-semibold text-foreground">
                    5 Newark Pompton Turnpike<br />Riverdale, NJ
                  </address>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-muted rounded-xl border border-border p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Operating Hours</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monday – Saturday</span>
                    <span className="font-medium text-foreground">3:00 AM – 8:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sunday</span>
                    <span className="font-medium text-foreground">Closed</span>
                  </div>
                </div>
              </div>

              {/* Lazy-loaded Google Map */}
              <LazyMap />
            </div>
          </div>
        </section>

        {/* Bottom strip */}
        <section className="py-12 px-6 md:px-10 bg-primary" aria-label="Call to action">
          <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/70 text-base font-light">
              Need transport <strong className="text-white font-semibold">today</strong>? Call (551) 326-9573.
            </p>
            <a href="tel:+15513269573" className="btn-primary shrink-0" aria-label="Call (551) 326-9573">
              <Icon name="PhoneIcon" size={16} />
              Call (551) 326-9573
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
